import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  Computer as ComputerIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { useChatStore } from '@/store/chatStore';
import { fetchMessages, markConversationRead, archiveConversation } from '@/api/chat';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useAgentsStore } from '@/store/agentsStore';
import ChatMessageBubble from '@/components/chat/ChatMessageBubble';

function formatDate(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'Hoy';
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Ayer';
  return d.toLocaleDateString();
}

export default function ChatConversationPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { send } = useWebSocket();
  const agents = useAgentsStore((s) => s.agents);

  const {
    conversations,
    messagesByConv,
    setMessages,
    updateConversationStatus,
    ensureConversation,
  } = useChatStore();

  const agent = agents.find((a) => a.agentId === agentId);
  const hostname = agent?.hostname ?? agentId ?? '';

  const conv = conversations.find((c) => c.agentId === agentId);
  const convId = conv?.conversationId;
  const messages = useMemo(
    () => (convId ? (messagesByConv[convId] ?? []) : []),
    [convId, messagesByConv]
  );

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Mientras no haya conversación todavía: no hay nada que cargar, mostramos el estado vacío.
  const showLoading = !!agentId && (resolving || (!!convId && loading));

  // Si el admin entra directo a /chat/:agentId (URL, refresh, link) el store de
  // conversaciones está vacío; lo hidratamos para poder traer el historial.
  useEffect(() => {
    if (!agentId) return;
    let cancelled = false;
    ensureConversation(agentId)
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, ensureConversation]);

  // Load messages when we have a conversationId
  useEffect(() => {
    if (!convId) return;
    let cancelled = false;
    fetchMessages(convId)
      .then((msgs) => {
        if (cancelled) return;
        setMessages(convId, msgs);
        setError(null);
        // Mark as read
        markConversationRead(convId).catch(() => {});
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [convId, setMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Mark as read when new messages arrive while page is open
  useEffect(() => {
    if (!convId || messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg?.senderType === 'agent' && !lastMsg.readAt) {
      markConversationRead(convId).catch(() => {});
    }
  }, [convId, messages]);

  const handleSend = useCallback(() => {
    const content = input.trim();
    if (!content || !agentId) return;
    setSending(true);
    const ok = send({ type: 'chat.send', data: { agentId, content } });
    if (ok) {
      setInput('');
    }
    setSending(false);
  }, [input, agentId, send]);

  const handleKeyDown = (e: { key: string; shiftKey: boolean; preventDefault: () => void }) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleArchive = async () => {
    if (!convId) return;
    try {
      await archiveConversation(convId);
      updateConversationStatus(convId, 'archived');
      navigate('/chat');
    } catch {}
  };

  // Group messages by date
  const grouped: Array<{ date: string; messages: typeof messages }> = [];
  for (const msg of messages) {
    const dateKey = formatDate(msg.createdAt);
    const last = grouped[grouped.length - 1];
    if (last && last.date === dateKey) {
      last.messages.push(msg);
    } else {
      grouped.push({ date: dateKey, messages: [msg] });
    }
  }

  return (
    <Box
      sx={{
        height: {
          // Mobile: AppBar 56px + main padding-bottom 24px (pt is 0 on xs).
          xs: 'calc(100dvh - 56px - 24px)',
          // sm+: AppBar 64px + main padding (24 top + 24 bottom = 48px).
          sm: 'calc(100dvh - 64px - 48px)',
          md: 'calc(100vh - 64px - 48px)',
        },
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <IconButton onClick={() => navigate('/chat')}>
          <ArrowBackIcon />
        </IconButton>
        <Avatar sx={{ bgcolor: 'primary.light', width: 36, height: 36 }}>
          <ComputerIcon fontSize="small" />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={600}>{hostname}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              size="small"
              label={agent?.connected ? 'Conectado' : 'Desconectado'}
              color={agent?.connected ? 'success' : 'default'}
              sx={{ height: 16, fontSize: '0.65rem' }}
            />
          </Box>
        </Box>
        {conv?.status === 'active' && (
          <Tooltip title="Archivar conversación">
            <IconButton onClick={handleArchive}>
              <ArchiveIcon />
            </IconButton>
          </Tooltip>
        )}
      </Paper>

      {/* Messages area */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
        {showLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!showLoading && !error && messages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">
              Sin mensajes aún. Inicia la conversación.
            </Typography>
          </Box>
        )}

        {grouped.map((group) => (
          <Box key={group.date}>
            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {group.date}
              </Typography>
            </Divider>
            {group.messages.map((msg) => (
              <ChatMessageBubble key={msg.messageId} msg={msg} />
            ))}
          </Box>
        ))}
        <div ref={bottomRef} />
      </Box>

      {/* Input area */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
          bgcolor: 'background.paper',
          p: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 1,
          alignItems: 'flex-end',
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder="Escribe un mensaje... (Enter para enviar)"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={sending}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!input.trim() || sending}
          sx={{ mb: 0.5 }}
        >
          <SendIcon />
        </IconButton>
      </Paper>
    </Box>
  );
}
